import { BUFFER_LOW_WATER, ICE_SERVERS } from '../config';
import { Emitter } from '../util/emitter';
import type { SignalingClient, SignalPayload } from './signaling';
import type { ConnectionState } from './types';

type PeerEvents = {
  /** DataChannel is open and ready for application messages. */
  channelopen: void;
  /** A control (string) or binary (ArrayBuffer) frame arrived. */
  message: string | ArrayBuffer;
  state: ConnectionState;
  error: Error;
  channelclose: void;
};

const CHANNEL_LABEL = 'file-transfer';

/**
 * PeerConnection — wraps RTCPeerConnection + a single reliable, ordered
 * DataChannel, wiring SDP/ICE through the SignalingClient.
 *
 * The `initiator` (sender) creates the channel and the offer; the other side
 * (receiver) answers and adopts the channel via `ondatachannel`. ICE candidates
 * are trickled and buffered until the remote description is applied.
 */
export class PeerConnection extends Emitter<PeerEvents> {
  private pc: RTCPeerConnection;
  private channel: RTCDataChannel | null = null;
  private pendingCandidates: RTCIceCandidateInit[] = [];
  private remoteSet = false;

  constructor(
    private readonly signaling: SignalingClient,
    private readonly initiator: boolean,
  ) {
    super();
    this.pc = this.createConnection();
    this.signaling.on('signal', this.onSignal);
  }

  private createConnection(): RTCPeerConnection {
    const pc = new RTCPeerConnection({ iceServers: ICE_SERVERS });

    pc.onicecandidate = (ev) => {
      if (ev.candidate) {
        this.signaling.send({ type: 'candidate', candidate: ev.candidate.toJSON() });
      }
    };

    pc.onconnectionstatechange = () => {
      const map: Record<RTCPeerConnectionState, ConnectionState> = {
        new: 'connecting',
        connecting: 'connecting',
        connected: 'connected',
        disconnected: 'disconnected',
        failed: 'failed',
        closed: 'closed',
      };
      this.emit('state', map[pc.connectionState]);
    };

    pc.ondatachannel = (ev) => {
      this.adoptChannel(ev.channel);
    };

    return pc;
  }

  /** Begin negotiation. Initiator creates the channel + offer immediately. */
  async start(): Promise<void> {
    if (!this.initiator) return; // receiver waits for the offer
    try {
      const channel = this.pc.createDataChannel(CHANNEL_LABEL, { ordered: true });
      this.adoptChannel(channel);
      const offer = await this.pc.createOffer();
      await this.pc.setLocalDescription(offer);
      this.signaling.send({ type: 'offer', sdp: offer.sdp ?? '' });
    } catch (err) {
      this.emit('error', err as Error);
    }
  }

  private adoptChannel(channel: RTCDataChannel): void {
    channel.binaryType = 'arraybuffer';
    channel.bufferedAmountLowThreshold = BUFFER_LOW_WATER;
    this.channel = channel;

    channel.onopen = () => this.emit('channelopen', undefined);
    channel.onclose = () => this.emit('channelclose', undefined);
    channel.onmessage = (ev: MessageEvent) => this.emit('message', ev.data);
    channel.onerror = (ev: Event) => {
      const e = ev as RTCErrorEvent;
      this.emit('error', e.error ?? new Error('DataChannel error'));
    };
  }

  private onSignal = async (payload: SignalPayload): Promise<void> => {
    try {
      if (payload.type === 'offer') {
        await this.pc.setRemoteDescription({ type: 'offer', sdp: payload.sdp });
        this.remoteSet = true;
        await this.drainCandidates();
        const answer = await this.pc.createAnswer();
        await this.pc.setLocalDescription(answer);
        this.signaling.send({ type: 'answer', sdp: answer.sdp ?? '' });
      } else if (payload.type === 'answer') {
        await this.pc.setRemoteDescription({ type: 'answer', sdp: payload.sdp });
        this.remoteSet = true;
        await this.drainCandidates();
      } else if (payload.type === 'candidate') {
        if (this.remoteSet) {
          await this.pc.addIceCandidate(payload.candidate);
        } else {
          this.pendingCandidates.push(payload.candidate);
        }
      }
      // 'resume' is handled at the transfer-engine layer, not here.
    } catch (err) {
      this.emit('error', err as Error);
    }
  };

  private async drainCandidates(): Promise<void> {
    const queued = this.pendingCandidates;
    this.pendingCandidates = [];
    for (const c of queued) {
      try {
        await this.pc.addIceCandidate(c);
      } catch {
        /* stale candidate; ignore */
      }
    }
  }

  // --- DataChannel I/O ------------------------------------------------------

  get dataChannel(): RTCDataChannel | null {
    return this.channel;
  }

  get bufferedAmount(): number {
    return this.channel?.bufferedAmount ?? 0;
  }

  send(data: string | ArrayBuffer | ArrayBufferView): void {
    if (!this.channel || this.channel.readyState !== 'open') {
      throw new Error('DataChannel is not open');
    }
    // Overloads on RTCDataChannel.send are awkward to satisfy with a union; the
    // runtime accepts all of string | ArrayBuffer | ArrayBufferView.
    (this.channel.send as (d: string | ArrayBuffer | ArrayBufferView) => void)(data);
  }

  /** Resolve once the send buffer drains below the low-water mark. */
  waitForDrain(): Promise<void> {
    const channel = this.channel;
    if (!channel || channel.bufferedAmount <= channel.bufferedAmountLowThreshold) {
      return Promise.resolve();
    }
    return new Promise((resolve) => {
      const handler = () => {
        channel.removeEventListener('bufferedamountlow', handler);
        resolve();
      };
      channel.addEventListener('bufferedamountlow', handler);
    });
  }

  /** Tear down a stale RTCPeerConnection for a fresh renegotiation (resume). */
  reset(): void {
    this.pendingCandidates = [];
    this.remoteSet = false;
    this.channel = null;
    try {
      this.pc.close();
    } catch {
      /* ignore */
    }
    this.pc = this.createConnection();
  }

  close(): void {
    this.signaling.off('signal', this.onSignal);
    try {
      this.channel?.close();
    } catch {
      /* ignore */
    }
    try {
      this.pc.close();
    } catch {
      /* ignore */
    }
    this.removeAll();
  }
}
